import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Skeleton, Button } from "../components/ui"
import { Shell } from "../components/shell"
import { CardDialog } from "../components/card-dialog"
import { BoardSettingsDialog } from "../components/board-settings-dialog"
import { NewCardDialog } from "../components/new-card-dialog"
import { LabelBadge } from "../components/label-badge"
import { useBoardSocket } from "../hooks/useBoardSocket"
import { Calendar, CheckSquare, Paperclip, MessageSquare, ChevronUp, ChevronDown, Lock, Settings } from "lucide-react"
import { LIMITS, getPlan } from "../lib/plans"

type CardRow = {
  id: string
  title: string
  description: string | null
  priority: string | null
  dueDate: string | null
  labels: string | null
  columnId: string
  columnName: string
  assignees?: { user: { id: string; name: string | null; email: string } }[]
  comments?: any[]
  attachments?: any[]
  checklistItems?: { id: string; checked: boolean }[]
}

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }
const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  low: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
}

type SortKey = "title" | "priority" | "dueDate" | "column" | "assignee"

/** List view of all cards on a board. Sortable and filterable table layout as an alternative to the kanban board. */
export default function ListPage() {
  const { workspaceId, projectId, boardId } = useParams({
    from: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId/list",
  })
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()
  const [sortKey, setSortKey] = useState<SortKey>("priority")
  const [sortAsc, setSortAsc] = useState(true)
  const [filterPriority, setFilterPriority] = useState("")
  const [search, setSearch] = useState("")
  const [openCardId, setOpenCardId] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [newCardOpen, setNewCardOpen] = useState(false)
  useBoardSocket(boardId)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (e.target as HTMLElement).isContentEditable) return
      if (e.key === "Escape") { setNewCardOpen(false); setOpenCardId(null); setSettingsOpen(false) }
      if (e.key === "/") { e.preventDefault(); document.querySelector<HTMLInputElement>("[data-global-search]")?.focus() }
      if (e.key === "n" || e.key === "N") { e.preventDefault(); setNewCardOpen(true) }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })
  const canTimeline = LIMITS[getPlan(workspace?.plan)].timeline

  const { data: board, isLoading } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () =>
      fetcher<{
        id: string
        name: string
        description: string | null
        projectId: string
        color: string | null
        labelColors: Record<string, string> | null
        columns: { id: string; name: string; cards: CardRow[] }[]
      }>(`/api/boards/${boardId}`),
    enabled: !!session,
    refetchInterval: 10000,
  })

  if (!session) {
    navigate({ to: "/login" })
    return null
  }

  if (isLoading || !board) {
    return (
      <Shell title="List" back={{ to: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId", label: "Board" }}>
        <main className="mx-auto w-full max-w-5xl space-y-3 p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </main>
      </Shell>
    )
  }

  const allCards: CardRow[] = board.columns.flatMap((col) =>
    col.cards.map((card) => ({ ...card, columnName: col.name }))
  )

  const filtered = allCards.filter((c) => {
    if (filterPriority && c.priority !== filterPriority) return false
    if (search && !c.title.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0
    if (sortKey === "title") cmp = a.title.localeCompare(b.title)
    else if (sortKey === "priority") cmp = (PRIORITY_ORDER[a.priority || ""] ?? 99) - (PRIORITY_ORDER[b.priority || ""] ?? 99)
    else if (sortKey === "dueDate") {
      const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
      const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
      cmp = da - db
    } else if (sortKey === "column") cmp = a.columnName.localeCompare(b.columnName)
    else if (sortKey === "assignee") cmp = (a.assignees?.[0]?.user.name || "").localeCompare(b.assignees?.[0]?.user.name || "")
    return sortAsc ? cmp : -cmp
  })

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((v) => !v)
    else { setSortKey(key); setSortAsc(true) }
  }

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k ? (sortAsc ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : null

  const exportCSV = () => {
    const rows: string[][] = [["Column", "Title", "Priority", "Assignee", "Due Date", "Labels"]]
    for (const card of allCards) {
      rows.push([card.columnName, card.title, card.priority || "", card.assignees?.length ? card.assignees.map((a) => a.user.name || a.user.email).join("; ") : "", card.dueDate ? new Date(card.dueDate).toLocaleDateString() : "", card.labels || ""])
    }
    const csv = rows.map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url; a.download = `${board.name}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"

  return (
    <Shell
      title={board.name}
      back={{ to: "/workspaces/$workspaceId/projects/$projectId", label: "Project" }}
      boardColor={board?.color}
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} title="Export as CSV">Export CSV</Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}><Settings className="mr-1 h-4 w-4" />Settings</Button>
          )}
        </div>
      }
      tabs={
        <div className="flex items-center justify-between">
          <div className="flex gap-4 text-sm">
          <Link
            to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId"
            params={{ workspaceId, projectId, boardId }}
            className="pb-2 text-muted-foreground hover:text-foreground"
          >
            Board
          </Link>
          <span className="border-b-2 border-primary pb-2 font-medium">List</span>
          <Link
            to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/calendar"
            params={{ workspaceId, projectId, boardId }}
            className="pb-2 text-muted-foreground hover:text-foreground"
          >
            Calendar
          </Link>
          {canTimeline ? (
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/timeline"
              params={{ workspaceId, projectId, boardId }}
              className="pb-2 text-muted-foreground hover:text-foreground"
            >
              Timeline
            </Link>
          ) : (
            <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="flex items-center gap-1 pb-2 text-muted-foreground/50 hover:text-muted-foreground" title="Pro feature">
              Timeline <Lock className="h-3 w-3" />
            </Link>
          )}
          </div>
          <span className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">N</kbd> new card
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">/</kbd> search
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd> close
          </span>
          <div />
        </div>
      }
    >
      <main className="flex flex-1 flex-col" style={board?.color ? { background: board.color } : undefined}>
        <div className="mx-auto w-full max-w-5xl p-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cards..."
            className="h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none"
          >
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <span className="ml-auto rounded-md border border-border bg-card/80 px-2 py-0.5 text-sm text-muted-foreground backdrop-blur-sm">{sorted.length} card{sorted.length !== 1 ? "s" : ""}</span>
        </div>

        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50 text-xs font-medium text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button className="flex items-center gap-1 hover:text-foreground" onClick={() => handleSort("title")}>
                    Title <SortIcon k="title" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button className="flex items-center gap-1 hover:text-foreground" onClick={() => handleSort("column")}>
                    Column <SortIcon k="column" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button className="flex items-center gap-1 hover:text-foreground" onClick={() => handleSort("priority")}>
                    Priority <SortIcon k="priority" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button className="flex items-center gap-1 hover:text-foreground" onClick={() => handleSort("assignee")}>
                    Assignee <SortIcon k="assignee" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button className="flex items-center gap-1 hover:text-foreground" onClick={() => handleSort("dueDate")}>
                    Due <SortIcon k="dueDate" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">Labels</th>
                <th className="px-4 py-3 text-left">Meta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No cards match your filters.</td>
                </tr>
              ) : sorted.map((card) => (
                <tr key={card.id} className="cursor-pointer transition-colors hover:bg-muted/40" onClick={() => setOpenCardId(card.id)}>
                  <td className="max-w-[220px] px-4 py-3">
                    <p className="truncate font-medium">{card.title}</p>
                    {card.description && (
                      <p className="truncate text-xs text-muted-foreground">{card.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{card.columnName}</td>
                  <td className="px-4 py-3">
                    {card.priority && (
                      <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${PRIORITY_COLORS[card.priority] || ""}`}>
                        {card.priority}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {card.assignees?.length ? card.assignees.map((a) => a.user.name || a.user.email).join(", ") : <span className="text-xs">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {card.dueDate ? (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(card.dueDate).toLocaleDateString()}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {card.labels ? card.labels.split(",").map((l) => l.trim()).filter(Boolean).map((label, i) => (
                        <LabelBadge key={label} label={label} color={board?.labelColors?.[label]} index={i} />
                      )) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      {card.comments && card.comments.length > 0 && (
                        <span className="flex items-center gap-0.5 text-xs"><MessageSquare className="h-3 w-3" />{card.comments.length}</span>
                      )}
                      {card.attachments && card.attachments.length > 0 && (
                        <span className="flex items-center gap-0.5 text-xs"><Paperclip className="h-3 w-3" />{card.attachments.length}</span>
                      )}
                      {card.checklistItems && card.checklistItems.length > 0 && (
                        <span className="flex items-center gap-0.5 text-xs">
                          <CheckSquare className="h-3 w-3" />
                          {card.checklistItems.filter((i) => i.checked).length}/{card.checklistItems.length}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      </main>
      <CardDialog
        open={!!openCardId}
        onClose={() => setOpenCardId(null)}
        cardId={openCardId}
        boardId={boardId}
        workspaceId={workspaceId}
      />
      <BoardSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        boardId={boardId}
        workspaceId={workspaceId}
      />
      <NewCardDialog
        open={newCardOpen}
        onClose={() => setNewCardOpen(false)}
        boardId={boardId}
      />
    </Shell>
  )
}
